const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm'];
const IMPORTED_FILE_FALLBACK_NAME = 'imported-audience.csv';
export const DIRECT_CONTACT_AUDIENCE_TYPE = 'Contact';
export const DIRECT_GROUP_AUDIENCE_TYPE = 'DirectGroup';
export const DEFAULT_CAMPAIGN_TEMPLATE_VARIABLES = [
  'name',
  'first_name',
  'last_name',
  'phone',
  'phone_number',
  'email',
  'id',
  'identifier',
  'today_date',
  'today_datetime',
  'today_time',
];
const RESERVED_IMPORTED_VARIABLES = new Set([
  'name',
  'nome',
  'first_name',
  'last_name',
  'id',
  'identifier',
  'phone_number',
  'phone_numero',
  'phone',
  'whatsapp',
  'telefone_celular',
  'telefone_principal',
  'telefone',
  'celular',
  'email',
  'today',
  'today_date',
  'today_datetime',
  'today_time',
  'current_date',
  'current_datetime',
  'current_time',
]);
export const DEFAULT_AI_REWRITE_PROMPT = `# Persona
Você é um copywriter senior. Sua função é receber uma mensagem de referência com um placeholder, personalizá-la com o nome de um cliente específico e gerar múltiplas variações criativas para evitar repetição.

# Objetivo
Reescreva a mensagem mantendo a essência do texto original, aplicando uma forma de escrita diferente, objetiva, cordial e profissional. Você pode usar emojis de forma profissional e elegante.

# Instruções obrigatórias
Substitua corretamente placeholders de nome do cliente quando existirem.
Se a mensagem original tiver links, eles devem permanecer exatamente como estão.
Não mude as informações essenciais da mensagem.
Crie uma variação natural, humana e com boa concordância verbal.
Pode variar entre mensagens mais curtas e mais longas.
Pode usar mais quebras de linha quando fizer sentido.
Não adicione "-", hífen, travessões ou marcas que deixem a mensagem com cara de robô.

# Saída
Retorne apenas a mensagem final reescrita.`;

const getFileExtension = value => {
  const source = value?.split('?')[0] || '';
  const parts = source.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const hasAttachment = message =>
  Boolean(
    message?.fileBlobId ||
      message?.file_blob_id ||
      message?.fileUrl ||
      message?.file_url
  );

export const inferCampaignMessageType = message => {
  if (!hasAttachment(message)) {
    return 'text';
  }

  const mimeType =
    message?.fileMimeType || message?.file_mime_type || message?.mimeType || '';
  const fileName = message?.fileName || message?.file_name || '';
  const fileUrl = message?.fileUrl || message?.file_url || '';
  const extension = getFileExtension(fileName || fileUrl);

  if (mimeType.startsWith('image/') || IMAGE_EXTENSIONS.includes(extension)) {
    return 'image';
  }

  if (mimeType.startsWith('video/') || VIDEO_EXTENSIONS.includes(extension)) {
    return 'video';
  }

  if (
    mimeType.startsWith('audio/') ||
    message?.isRecordedAudio ||
    message?.is_recorded_audio ||
    AUDIO_EXTENSIONS.includes(extension)
  ) {
    return 'audio';
  }

  return 'file';
};

export const createEmptyCampaignMessage = (
  order,
  idFactory = index => Date.now() + index
) => ({
  id: idFactory(order),
  order,
  type: 'text',
  content: '',
  caption: '',
  fileUrl: null,
  fileName: '',
  fileBlobId: null,
  fileMimeType: '',
  isRecordedAudio: false,
  useAiGeneration: false,
  aiRewritePrompt: DEFAULT_AI_REWRITE_PROMPT,
  delayAfterSeconds: null,
});

export const normalizeCampaignMessage = (
  message,
  index,
  idFactory = order => Date.now() + order
) => {
  const normalized = {
    id: idFactory(index),
    order: message?.order ?? index,
    content: message?.content || '',
    caption: message?.caption || '',
    fileUrl: message?.file_url || message?.fileUrl || null,
    fileName: message?.file_name || message?.fileName || '',
    fileBlobId: message?.file_blob_id || message?.fileBlobId || null,
    fileMimeType: message?.file_mime_type || message?.fileMimeType || '',
    isRecordedAudio: Boolean(
      message?.is_recorded_audio || message?.isRecordedAudio
    ),
    useAiGeneration: Boolean(
      message?.use_ai_generation || message?.useAiGeneration
    ),
    // Use nullish coalescing so an intentionally-cleared prompt ('') stays empty
    // on load. Only a truly absent prompt (undefined/null) falls back to the
    // default persona. Previously `||` forced any empty prompt back to DEFAULT,
    // so the field could never be saved blank.
    aiRewritePrompt:
      message?.ai_rewrite_prompt ??
      message?.aiRewritePrompt ??
      DEFAULT_AI_REWRITE_PROMPT,
    delayAfterSeconds:
      message?.delay_after_seconds ?? message?.delayAfterSeconds ?? null,
  };

  return {
    ...normalized,
    type: message?.type || inferCampaignMessageType(normalized),
  };
};

export const serializeCampaignMessage = message => {
  const type = inferCampaignMessageType(message);
  const content = message?.content || '';

  return {
    order: message.order,
    type,
    content,
    caption: type === 'text' ? '' : content,
    file_url: message.fileUrl,
    file_name: message.fileName,
    file_blob_id: message.fileBlobId,
    file_mime_type: message.fileMimeType,
    is_recorded_audio: Boolean(message.isRecordedAudio),
    use_ai_generation: Boolean(message.useAiGeneration),
    // Persist exactly what the user typed — including an empty string when they
    // deliberately clear the prompt. Forcing `|| DEFAULT` here reverted a cleared
    // field back to the default persona on every save (and on "zerar e disparar").
    ai_rewrite_prompt: message.aiRewritePrompt ?? '',
    delay_after_seconds: message.delayAfterSeconds,
  };
};

export const buildCampaignAudienceTargetValue = (type, id) =>
  `${type}:${String(id)}`;

export const parseCampaignAudienceTargetValue = value => {
  if (typeof value === 'string') {
    const [type, ...rest] = value.split(':');

    if (
      [DIRECT_CONTACT_AUDIENCE_TYPE, DIRECT_GROUP_AUDIENCE_TYPE].includes(
        type
      ) &&
      rest.length
    ) {
      return {
        type,
        id: rest.join(':'),
      };
    }
  }

  return {
    type: DIRECT_CONTACT_AUDIENCE_TYPE,
    id: value,
  };
};

export const normalizeSelectedInboxIds = inboxIds =>
  Array.from(
    new Set(
      (Array.isArray(inboxIds) ? inboxIds : [])
        .map(id => Number(id))
        .filter(Boolean)
    )
  );

export const normalizeSelectedSenderValues = senderValues =>
  Array.from(
    new Set(
      (Array.isArray(senderValues) ? senderValues : [])
        .map(value => String(value || '').trim())
        .filter(Boolean)
    )
  );

export const selectedInboxIdsEqual = (left, right) => {
  const normalizedLeft = normalizeSelectedInboxIds(left);
  const normalizedRight = normalizeSelectedInboxIds(right);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((value, index) => value === normalizedRight[index])
  );
};

export const selectedSenderValuesEqual = (left, right) => {
  const normalizedLeft = normalizeSelectedSenderValues(left);
  const normalizedRight = normalizeSelectedSenderValues(right);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((value, index) => value === normalizedRight[index])
  );
};

export const resolveCampaignTemplateVariables = importedHeaders => {
  const importedVariables = (
    Array.isArray(importedHeaders) ? importedHeaders : []
  )
    .map(variable => String(variable || '').trim())
    .filter(variable => variable && !RESERVED_IMPORTED_VARIABLES.has(variable));

  return Array.from(
    new Set([...DEFAULT_CAMPAIGN_TEMPLATE_VARIABLES, ...importedVariables])
  );
};

export const normalizeCampaignAudience = audience => {
  const normalizedAudience = Array.isArray(audience) ? audience : [];
  const labelIds = normalizedAudience
    .filter(entry => entry?.type === 'Label')
    .map(entry => entry.id);
  const groupIds = normalizedAudience
    .filter(entry => entry?.type === 'Group')
    .map(entry => entry.id);
  const contactIds = normalizedAudience
    .filter(entry => entry?.type === 'Contact')
    .map(entry =>
      buildCampaignAudienceTargetValue(DIRECT_CONTACT_AUDIENCE_TYPE, entry.id)
    );
  const directGroupIds = normalizedAudience
    .filter(entry => entry?.type === DIRECT_GROUP_AUDIENCE_TYPE)
    .map(entry =>
      buildCampaignAudienceTargetValue(DIRECT_GROUP_AUDIENCE_TYPE, entry.id)
    );
  const importedContacts = normalizedAudience.filter(
    entry => entry?.type === 'ImportedContact'
  );

  const importedHeaders = Array.from(
    new Set(
      importedContacts.flatMap(entry =>
        Object.keys(entry || {}).filter(
          key =>
            !['type', 'source', 'import_file_name'].includes(key) && entry[key]
        )
      )
    )
  );

  const importFileName =
    importedContacts.find(entry => entry?.import_file_name)?.import_file_name ||
    (importedContacts.length ? IMPORTED_FILE_FALLBACK_NAME : '');

  return {
    mode: importedContacts.length ? 'import' : 'existing',
    labelIds,
    groupIds,
    contactIds: [...contactIds, ...directGroupIds],
    importFileName,
    importedHeaders,
    importedContacts,
  };
};

export const extractContactPayload = responseData =>
  responseData?.payload || responseData || null;

export const getImportedFileFallbackName = () => IMPORTED_FILE_FALLBACK_NAME;
